import { action, makeObservable, observable } from 'mobx';
import * as React from 'react';
import { observer } from 'mobx-react';
import { cs } from '@shared/utils/utils-classname';
import { DateSpan } from '@shared/ui-react/UtilComponents';
import { ScreenshotCommentsList } from './SavedStateCommentsList';
import { ISavedStateComment, SavedStateCommentsService } from './SavedStateCommentsService';
import { CanvasDrawing } from './CanvasDrawing';
import { CanvasUtils } from '@shared/utils/CanvasUtils';
import AttachDocumentsAndLinks from '@shared/attachments/AttachDocumentsAndLinks';
import { CollapsiblePanel } from '@shared/ui-react/CollapsiblePanel';
import { SavedViewAttachmentsService } from '@shared/attachments/SavedViewAttachmentsService';
import { Notify } from '@shared/ui-native/Notify';
import './SavedStateCommentsPanel.scss';
import { Events } from '@shared/Events';
import { Tooltip } from 'primereact/tooltip';
import { UserGroupsService } from '@shared/users/user-groups/UserGroupsService';
import { Coordinate } from 'ol/coordinate';
import { Button } from '@shared/ui-react/Button';
import { UsersService } from '@shared/services/UsersService';
import TaggingTextField from '@shared/user-state/comments/mentionTextField/TaggingTextFIeld';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { PageStateShare } from '@shared/user-state/ui/PageStateShare';


// import { UserRoleService } from "@shared/services/UserRoleService";

interface IState {
    users: IUser[];
    isLoading: boolean;
}

interface IProps {
    page: 'MAP2D' | 'MAP3D';
    pageState: IMapPageState;
    canvasDrawing: CanvasDrawing;
    drawingsDisplay: IDrawingsDisplay;
    treeData: ITreeData;
    onClose: (restorePreviousState?: boolean) => void;
    onResetView: (state: IMapPageState) => void;
    calculateDrawingLatLon?: (pixelPos: Coordinate) => [number, number];
    getMapAngle?: () => number;
    onUpdateState?: (state: IMapPageState) => void;
    onDownloadSavedView?: (state: IMapPageState) => void;
    onShowInPanel?: (state: IMapPageState) => void;
    hideHeader?: boolean;
}

@observer
export class SavedStateCommentsPanel extends React.Component<IProps> {
    commentsList: ScreenshotCommentsList;
    @observable inputText = '';
    @observable mentionedUsers: string[] = [];
    @observable commentStateUrl: string;
    @observable drawMode: boolean = false;
    @observable isSending: boolean = false;
    @observable isHidden: boolean = false;
    @observable isMinimized: boolean = false;
    @observable proceedToComment: boolean = false;
    @observable canDraw: boolean = true; // can't draw on 3d views when they are loaded on 2d map and vice versa
    @observable isAttachmentsOpen: boolean = !this.isTablet;
    @observable groupUuid: string;
    @observable groupsList: IUserGroup[] = null;
    // @observable roleUuid: string;
    // @observable rolesList: IUserRole[] = null;

    state: IState = {
        users: [],
        isLoading: false,
    };

    constructor(props: IProps) {
        super(props);
        makeObservable(this);

        Events.on('saved-views-close-drawings', () => this.closePanel());
    }

    get canSubmit() {
        return this.inputText.length > 0 || !this.props.canvasDrawing.isCanvasBlank;
    }

    componentDidUpdate(prevProps) {
        if (prevProps.pageState?.id !== this.props.pageState?.id) {
            this.drawMode = false;
            this.props.canvasDrawing.endDrawing();
            this.isHidden = false;
            this.isMinimized = false;
        }
        this.canDraw = this.props.pageState?.type === (this.props.page === 'MAP2D' ? '2D' : '3D');

        const urlRoot = location.origin + '/kuura/';
        this.commentStateUrl = urlRoot + (this.props.page === 'MAP2D' ? 'main.htm?state=' : 'mapvisualization.htm?state=') + prevProps.pageState?.uuid;
    }

    componentDidMount() {
        this.isHidden = false;
        this.isMinimized = false;
        this.drawMode = false;
        this.canDraw = this.props.pageState?.type === (this.props.page === 'MAP2D' ? '2D' : '3D');

        UserGroupsService.fetchGroupsFromKuura(kuura.getProjectId())
            .then(groups => (this.groupsList = groups))
            .fail(e => Notify.error(e.response?.data?.error));

        /*UserRoleService.fetchRoles(kuura.getProjectOrganization().uuid)
            .then(roles => this.rolesList = roles)
            .catch(e => Notify.error(e.response?.data?.error));*/

        UsersService.fetchUsersWithEmails()
            .then(users => {
                this.setState({
                    users,
                });
            })
            .fail(e => Notify.error(e.response?.data?.error));
    }

    componentWillUnmount() {
        this.props.drawingsDisplay.destroy();
        this.props.canvasDrawing.endDrawing();
    }

    @action.bound
    toggleDrawMode() {
        if (!this.canDraw) return;

        const newState = !this.drawMode;
        if (newState) {
            this.inputText = '';
            this.mentionedUsers = [];
            this.commentStateUrl = '';
            this.props.onResetView(this.props.pageState);
            this.props.canvasDrawing.startDrawing({ id: this.props.pageState.id });
            if (this.isTablet) {
                this.proceedToComment = true;
                this.isMinimized = true;
            }
        } else {
            this.props.canvasDrawing.endDrawing();
            this.proceedToComment = false;
        }
        this.drawMode = newState;
    }

    @action.bound
    closePanel(restorePreviousState = false) {
        this.props.drawingsDisplay.destroy();
        this.props.canvasDrawing.endDrawing();

        if (this.drawMode) {
            this.toggleDrawMode();
        }
        this.isHidden = true;

        if (this.props.onClose) {
            this.props.onClose(restorePreviousState);
        }
    }

    render() {
        const { pageState } = this.props;
        const canDownload = typeof this.props.onDownloadSavedView === 'function' && kuura.hasPrivilege('Download_Model');
        const canOpenInPanel = typeof this.props.onShowInPanel === 'function';
        const canUpdate = typeof this.props.onUpdateState === 'function' && pageState.user?.id === kuura.getUserId();

        return (
            <div id='view-properties' className={cs('of-a', { 'is-hidden': this.isHidden, 'is-minimized': this.isMinimized })}>
                {!this.props.hideHeader && (
                    <>
                        <div id='view-properties-options'>
                            <Dropdown closeOnBodyClick={true} className='ik-button-small ik-button-secondary--subtle'>
                                <i className='iki iki-more' />
                                <ul className='clickable'>
                                    {canUpdate ? (
                                        <li onClick={() => this.props.onUpdateState(pageState)}>{__('js_saved_views.update_saved_view')}</li>
                                    ) : (
                                        <li className='disabled' title={__('js_saved_views.update_disabled', pageState.user?.username)}>
                                            {__('js_saved_views.update_saved_view')}
                                        </li>
                                    )}
                                    {canDownload && <li onClick={() => this.props.onDownloadSavedView(pageState)}>{__('general.download_files')}</li>}
                                    {canOpenInPanel && <li onClick={() => this.props.onShowInPanel(pageState)}>{__('js_global_search.actions.open_in_folder')}</li>}
                                    <li onClick={() => PageStateShare.open(pageState)}>{__('general.share')}</li>
                                </ul>
                            </Dropdown>
                            <Button
                                onClick={() => this.closePanel(true)}
                                icon='iki iki-chevron-left'
                                className='ik-button-small ik-button-secondary--subtle'
                                tooltip={__('js_saved_views.close_restore_previous')}
                            />
                            {this.isMinimized && (
                                <Button icon='iki iki-expand' className='ik-button-small ik-button-secondary--subtle' onClick={() => (this.isMinimized = false)} />
                            )}
                            {!this.isMinimized && <Button icon='iki iki-minus' className='ik-button-small ik-button-secondary--subtle' onClick={() => (this.isMinimized = true)} />}
                            <Button onClick={() => this.closePanel(false)} icon='iki iki-cross' className='ik-button-small ik-button-secondary--subtle' />
                        </div>
                    </>
                )}

                {pageState.name && pageState.name.length && (
                    <div style={{ marginLeft: 10, marginBottom: 10 }}>
                        <strong>{pageState.name}</strong>
                    </div>
                )}

                {this.canDraw ? (
                    <section className={cs('ss-draw-wrapper', { on: this.drawMode })}>
                        {this.proceedToComment && (
                            <Button
                                icon='caret'
                                className='btn-primary'
                                onClick={() => {
                                    this.proceedToComment = false;
                                    this.isMinimized = false;
                                }}
                                style={{ position: 'absolute', top: 4, left: 13 }}
                            >
                                {__('js_saved_views.proceed_to_comment')}
                            </Button>
                        )}
                        <nav>
                            <div className='flex-row ai-center'>
                                {!this.drawMode && (
                                    <Button icon='iki iki-edit' onClick={this.toggleDrawMode}>
                                        {__('js_saved_views.send_comment_draw')}
                                    </Button>
                                )}

                                {!this.drawMode && this.props.page !== 'MAP2D' && (
                                    <>
                                        <Tooltip target='.saved-views__zoom-level-warning' baseZIndex={99999}>
                                            {__('js_saved_views.zoom_level_annotations_warning')}
                                        </Tooltip>
                                        <i className='iki iki-help saved-views__zoom-level-warning' style={{ marginLeft: 10 }} />
                                    </>
                                )}
                            </div>
                        </nav>

                        <form style={{ display: this.drawMode ? 'block' : 'none' }} onSubmit={e => this.handleSubmit(e)}>
                            <TaggingTextField
                                className='saved-view-mention'
                                placeholder={__('general.comment') + ' - ' + __('general.mention_people_placeholder')}
                                data={this.state.users.map(e => {
                                    return { display: e.fullName, id: e.uuid };
                                })}
                                value={this.inputText}
                                onChange={e => {
                                    if (e.target.value.replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1').length < 1000) {
                                        this.inputText = e.target.value;
                                    } else {
                                        Notify.warn(__('general.validation.maximum_n_field_length', '1000'));
                                    }
                                }}
                            />
                            <div style={{ marginTop: 10 }} className='flex-row ai-center'>
                                <span style={{ flexShrink: 0, marginLeft: 'auto' }}>{__('general.validation.visible_to_user_group_x')}: </span>
                                <select style={{ flexShrink: 1, marginLeft: 10, maxWidth: 130 }} value={this.groupUuid} onChange={e => (this.groupUuid = e.currentTarget.value)}>
                                    <option value='-1'>{__('general.all')}</option>
                                    {this.groupsList?.map(group => (
                                        <option value={group.uuid} key={group.uuid}>
                                            {group.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {/*<div style={{ marginTop: 10 }}>
                                <span style={{ verticalAlign: 'text-top' }}>Visible to user role: </span>
                                <select value={this.roleUuid} onChange={ e => this.roleUuid = e.currentTarget.value }>
                                    <option value='-1'>{__('general.all')}</option>
                                    {this.rolesList?.map(role =>
                                        <option value={role.uuid} key={role.uuid}>
                                            {role.name}
                                        </option>
                                    )}
                                </select>
                            </div>*/}

                            <div className='flex-row' style={{ justifyContent: 'space-between' }}>
                                <Button icon='iki iki-cross' onClick={this.toggleDrawMode}>
                                    {__('general.cancel')}
                                </Button>
                                <Button type='submit' className='btn-primary' disabled={!this.canSubmit} loading={this.isSending}>
                                    {__('general.send')}
                                </Button>
                            </div>
                        </form>
                    </section>
                ) : (
                    <div>{__('js_saved_views.cannot_draw_on_2d_or_3d_map', this.props.page === 'MAP2D' ? '3D' : '2D')}</div>
                )}

                <ScreenshotCommentsList
                    commentListUuid={pageState.uuid}
                    commentsOnly={!this.canDraw}
                    inFreeDrawing={this.drawMode}
                    isLegacy={pageState.page === 'MAP3D_LEGACY'}
                    onCommentVisible={this.restoreState}
                    ref={el => (this.commentsList = el)}
                    drawingsDisplay={this.props.drawingsDisplay}
                    isPageType2D={this.props.pageState?.type === '2D'}
                />
                <div style={{ marginTop: 15 }}>
                    <CollapsiblePanel
                        label={__('js_visualization.attached_documents_and_links')}
                        isOpen={this.isAttachmentsOpen}
                        isLoading={this.state.isLoading}
                        onToggle={b => (this.isAttachmentsOpen = b)}
                    >
                        <AttachDocumentsAndLinks
                            modelUuid={pageState.uuid}
                            getAttachments={(modelUuid: string) => {
                                this.setState({ isLoading: true });
                                return SavedViewAttachmentsService.getAttachments(modelUuid).done(() => this.setState({ isLoading: false }));
                            }}
                            addNewAttachment={(modelUuid: string, data: IAttachmentData) => {
                                this.setState({ isLoading: true });
                                return SavedViewAttachmentsService.addNewAttachment(modelUuid, data).done(() => this.setState({ isLoading: false }));
                            }}
                            deleteAttachment={(attachmentId: string, modelUuid: string) => {
                                this.setState({ isLoading: true });
                                return SavedViewAttachmentsService.deleteAttachment(attachmentId, modelUuid).done(() => this.setState({ isLoading: false }));
                            }}
                            treeData={this.props.treeData}
                        />
                    </CollapsiblePanel>
                </div>
                <div id='view-user'>
                    {'Created by: ' + (pageState.user ? pageState.user.username : pageState.username) + ' on '} <DateSpan timestamp={pageState.timestamp} />
                </div>
            </div>
        );
    }

    restoreState = () => this.props.onResetView(this.props.pageState);

    handleSubmit(e) {
        e.preventDefault();
        let canvasForSavingB64: string = null;
        let latLon = [null, null];

        if (!this.props.canvasDrawing.isCanvasBlank) {
            if (this.props.page === 'MAP2D') {
                const trimmedCanvas = CanvasUtils.trimEmptySpace(this.props.canvasDrawing.getCanvas());
                const bb = trimmedCanvas;
                const center: [number, number] = [bb.bound.left + bb.newWidth / 2, bb.bound.top + bb.newHeight / 2];
                latLon = this.props.calculateDrawingLatLon(center);
                const mapAngleRad = this.props.getMapAngle ? this.props.getMapAngle() : 0;
                const mapAngle = ((((mapAngleRad * 180) / Math.PI) % 360) + 360) % 360; // map angle can be negative
                if (mapAngle === 0) {
                    canvasForSavingB64 = trimmedCanvas.canvas.toDataURL();
                } else {
                    canvasForSavingB64 = CanvasUtils.rotateCanvas(trimmedCanvas.canvas, 360 - mapAngle).toDataURL();
                }
            } else {
                canvasForSavingB64 = this.props.canvasDrawing.getCanvas().toDataURL();
            }
        }

        const mentionedUsers: string[] = [];
        const regexExp = /\(([0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12})\)/gi;

        const matches = this.inputText.match(regexExp);
        matches?.forEach(match => {
            mentionedUsers.push(match.slice(1, -1));
        });

        const formattedText = this.inputText.replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1');

        const data: ISavedStateComment = {
            date: Date.now(),
            author: kuura.getUserId() as any,
            canvas: canvasForSavingB64,
            text: formattedText,
            mentionedUsers: !!mentionedUsers.length ? mentionedUsers : undefined,
            commentStateUrl: this.commentStateUrl,
            id: this.props.pageState.id,
            sourcePage: this.props.page,
            lat: latLon[0],
            lon: latLon[1],
            groupUuid: this.groupUuid !== '-1' ? this.groupUuid : undefined,
            // roleUuid: this.roleUuid !== '-1' ? this.roleUuid : undefined,
        };

        this.isSending = true;
        SavedStateCommentsService.create(data, this.props.pageState.page === 'MAP3D_LEGACY')
            .done(() => {
                this.toggleDrawMode();
                Notify.success();
                this.commentsList.fetch();
                this.groupUuid = '-1';
            })
            .fail(Notify.error)
            .always(() => {
                this.isSending = false;
                this.props.canvasDrawing.endDrawing();
            });
    }

    get isTablet() {
        return window.innerWidth < 900;
    }
}
